/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import logo from "@packages/assets/logo.png";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";

import type { LessonPlanOutput as DailyLessonPlanOutputType } from "../types/LessonPlan";
import { CourseData } from "@packages/constant/CourseData";
import { LessonPlannerSectionDto } from "@packages/api/madlenAPI.schemas";

interface LessonPlanPDFProps {
  initialData: DailyLessonPlanOutputType;
  planType: "MAARIF" | "DAILY";
}

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Montserrat",
    backgroundColor: "#ffffff",
  },
  header: {
    marginBottom: 30,
    backgroundColor: "#f8fafc",
    padding: 20,
    borderRadius: 8,
    borderBottom: "2 solid #e2e8f0",
    flexDirection: "row",
    alignItems: "center",
  },
  headerContent: {
    flex: 1,
  },
  logo: {
    width: 40,
    height: 40,
    marginRight: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 10,
    color: "#1e40af",
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    color: "#64748b",
    marginBottom: 5,
    textAlign: "center",
  },
  subtitle2: {
    fontSize: 12,
    color: "#64748b",
    marginBottom: 5,
    textAlign: "center",
  },
  metadataContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#f1f5f9",
    padding: 15,
    borderRadius: 8,
    marginBottom: 25,
  },
  metadataItem: {
    flex: 1,
    alignItems: "center",
  },
  metadataLabel: {
    fontSize: 10,
    color: "#64748b",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  metadataValue: {
    fontSize: 12,
    color: "#0f172a",
    fontWeight: "bold",
  },
  section: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1 solid #e2e8f0",
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: 8,
    borderBottom: "1 solid #e2e8f0",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1e40af",
  },
  sectionDuration: {
    fontSize: 11,
    color: "#64748b",
  },
  arrangementSection: {
    marginBottom: 10,
  },
  arrangementTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#0f172a",
    marginBottom: 8,
  },
  contentList: {
    marginLeft: 15,
  },
  listItem: {
    fontSize: 11,
    color: "#334155",
    marginBottom: 6,
    lineHeight: 1.4,
  },
  footer: {
    marginTop: 30,
    padding: "10 15",
    fontSize: 10,
    color: "#64748b",
    textAlign: "center",
    borderTop: "1 solid #e2e8f0",
  },
});

const DailyLessonPlanDocument = ({
  data,
}: {
  data: DailyLessonPlanOutputType;
}) => {
  const { t, i18n } = useTranslation();
  const isPreSchool = data.gradeLevel === -3 || data.gradeLevel === -2;
  const grade = isPreSchool
    ? null
    : t("dailyLessonPlan.grade", { grade: data.gradeLevel });

  return (
    <Document title={`${t("dailyLessonPlan.title")} - ${data.gradeLevel}`}>
      <Page size="A4" style={styles.page} wrap>
        {/* Header - Always render */}
        <View style={styles.header}>
          <Image style={styles.logo} src={logo} />
          <View style={styles.headerContent}>
            <Text style={styles.title}>{t("dailyLessonPlan.title")}</Text>
            {data.gradeLevel && data.lesson && (
              <Text style={styles.subtitle}>
                {grade && grade + " - "}
                {t(
                  `courseType.${CourseData[
                    data.lesson as keyof typeof CourseData
                  ]?.name(data.gradeLevel ?? 8)}`,
                )}{" "}
                -{" "}
                {Object.values(data.sections).reduce((total, section) => {
                  const sectionDuration = Number(section.duration) || 0;
                  return total + sectionDuration;
                }, 0)}{" "}
                {t("common.time.minutes")}
              </Text>
            )}
            <Text style={styles.subtitle2}>
              {format(new Date(), "dd MMMM yyyy", {
                locale: i18n.language === "tr" ? tr : enUS,
              })}
            </Text>
          </View>
        </View>

        {/* Learning Outcomes - Only if there are outcomes */}
        {data.learningOutcomes?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t("dailyLessonPlan.learningOutcomes")}
            </Text>
            <View style={styles.contentList}>
              {data.learningOutcomes.map((outcome, index) => (
                <Text key={index} style={styles.listItem}>
                  • {outcome.text}
                </Text>
              ))}
            </View>
          </View>
        )}

        {/* Main Sections - Only render sections with content */}
        {Object.entries(data.sections).map(
          ([key, section]: [string, LessonPlannerSectionDto]) =>
            section.content.length > 0 && (
              <View key={key} wrap={false} style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>
                    {t(`dailyLessonPlan.output.sections.${key.toLowerCase()}`)}
                  </Text>
                  {section.duration && Number(section.duration) > 0 && (
                    <Text style={styles.sectionDuration}>
                      {section.duration} {t("common.time.minutes")}
                    </Text>
                  )}
                </View>
                <View style={styles.contentList}>
                  {section.content.map((item, index) => (
                    <Text key={index} style={styles.listItem}>
                      • {item.details}
                    </Text>
                  ))}
                </View>
              </View>
            ),
        )}

        {/* Additional Arrangements - Only if there are arrangements */}
        {Object.values(data.additionalArrangements).some(
          (arr) => Array.isArray(arr) && arr.length > 0,
        ) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t("dailyLessonPlan.additional.arrangements.title")}
            </Text>
            {Object.entries(data.additionalArrangements).map(
              ([key, arrangement]) =>
                arrangement.length > 0 && (
                  <View key={key} style={{ marginBottom: 10 }}>
                    <Text style={[styles.listItem, { fontWeight: "bold" }]}>
                      {t(
                        `dailyLessonPlan.additional.arrangements.${key}.title`,
                      )}
                    </Text>
                    <View style={styles.contentList}>
                      {arrangement.map((suggestion: string, index: number) => (
                        <Text key={index} style={styles.listItem}>
                          • {suggestion}
                        </Text>
                      ))}
                    </View>
                  </View>
                ),
            )}
          </View>
        )}

        {/* Additional Instructions - Only if they exist */}
        {data.additionalInstructions && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t("dailyLessonPlan.additional.instructions.title")}
            </Text>
            <Text style={styles.listItem}>{data.additionalInstructions}</Text>
          </View>
        )}
      </Page>
    </Document>
  );
};

const MaarifLessonPlanDocument = ({
  data,
}: {
  data: DailyLessonPlanOutputType;
}) => {
  const { t, i18n } = useTranslation();
  const isPreSchool = data.gradeLevel === -3 || data.gradeLevel === -2;
  const grade = isPreSchool
    ? null
    : t("dailyLessonPlan.grade", { grade: data.gradeLevel });
  return (
    <Document
      title={`${t("dailyLessonPlan.maarifTitle")} - ${data.gradeLevel}`}
    >
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header}>
          <Image style={styles.logo} src={logo} />
          <View style={styles.headerContent}>
            <Text style={styles.title}>{t("dailyLessonPlan.maarifTitle")}</Text>
            <Text style={styles.subtitle}>
              {grade && grade + " - "}
              {t(
                `courseType.${CourseData[
                  data.lesson as keyof typeof CourseData
                ]?.name(data.gradeLevel ?? 8)}`,
              )}{" "}
              -{" "}
              {Object.values(data.sections).reduce((total, section) => {
                const sectionDuration = Number(section.duration) || 0;
                return total + sectionDuration;
              }, 0)}{" "}
              {t("common.time.minutes")}
            </Text>
            <Text style={styles.subtitle2}>
              {format(new Date(), "dd MMMM yyyy", {
                locale: i18n.language === "tr" ? tr : enUS,
              })}
            </Text>
          </View>
        </View>

        {data.learningOutcomes?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t("dailyLessonPlan.learningObjectives")}
            </Text>
            <View style={styles.contentList}>
              {data.learningOutcomes.map((objective, index) => (
                <Text key={index} style={styles.listItem}>
                  • {objective.text}
                </Text>
              ))}
            </View>
          </View>
        )}

        {Object.entries(data.sections).map(
          ([key, section]: [string, LessonPlannerSectionDto]) =>
            section.content.length > 0 && (
              <View key={key} wrap={false} style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>
                    {t(`dailyLessonPlan.output.sections.${key.toLowerCase()}`)}
                  </Text>
                  {section.duration && Number(section.duration) > 0 && (
                    <Text style={styles.sectionDuration}>
                      {section.duration} {t("common.time.minutes")}
                    </Text>
                  )}
                </View>
                <View style={styles.contentList}>
                  {section.content.map((item, index) => (
                    <Text key={index} style={styles.listItem}>
                      • {item.details}
                    </Text>
                  ))}
                </View>
              </View>
            ),
        )}

        {Object.values(data.additionalArrangements).some(
          (arr) => Array.isArray(arr) && arr.length > 0,
        ) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t("dailyLessonPlan.additional.arrangements.title")}
            </Text>
            {Object.entries(data.additionalArrangements).map(
              ([key, arrangement]) =>
                arrangement.length > 0 && (
                  <View key={key} style={{ marginBottom: 10 }}>
                    <Text style={[styles.listItem, { fontWeight: "bold" }]}>
                      {t(
                        `dailyLessonPlan.additional.arrangements.${key}.title`,
                      )}
                    </Text>
                    <View style={styles.contentList}>
                      {arrangement.map((suggestion: string, index: number) => (
                        <Text key={index} style={styles.listItem}>
                          • {suggestion}
                        </Text>
                      ))}
                    </View>
                  </View>
                ),
            )}
          </View>
        )}
      </Page>
    </Document>
  );
};

function LessonPlanPDF({ initialData, planType }: LessonPlanPDFProps) {
  if (planType === "DAILY") {
    console.log(initialData, "DAILY PDF");
    return <DailyLessonPlanDocument data={initialData} />;
  }
  return <MaarifLessonPlanDocument data={initialData} />;
}

export default LessonPlanPDF;
